import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useReplenishment } from '../../hooks/useReplenishment'
import { useCompanyBudget } from '../../hooks/useCompanyBudget'
import { useInstallments } from '../../hooks/useInstallments'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'
import { useAiInsights, type SupplierAnalysis } from '../../hooks/useAiInsights'
import { useIsMobile } from '../../hooks/useIsMobile'
import SupplierAnalysisModal from '../../components/purchase/SupplierAnalysisModal'
import MobilePurchaseSuggestion from '../../components/purchase/mobile/MobilePurchaseSuggestion'
import { commercialStatusColor, AI_BTN, AI_PANEL, AI_LABEL } from '../../lib/statusStyles'
import { aplicarDobraRuptura, isRupturaAcionavel } from '../../lib/replenishment'
import { isAfterCutoff, splitByCashCap, type CashCapItem } from '../../lib/budgetCap'
import type { CommercialStatus, KanbanStatusOp, ProductRow } from '../../types'

// Cockpit do Motor de Sugestão de Compras: lista priorizada por score, agrupada por
// fornecedor, com ações que reusam as mesmas mutações do Painel BX. Itens aprovados
// fluem para a aba "Emissão de Pedidos".

const STATUS_ORDER: CommercialStatus[] = ['Compra urgente', 'Comprar', 'Monitorar', 'Não comprar']

// Item "acionável": ainda precisa de compra — sem pedido em aberto e não aprovado.
// Mesmo critério que a lista (candidates) e o chip "Em risco" (atRiskCount) aplicam;
// centralizado aqui para que a contagem dos chips nunca divirja do que é listado.
const isActionable = (r: ProductRow) =>
  !r.active_order_number && r.kanban_status !== 'Compra Aprovada'

const fmtMoney = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const fmtNum1 = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

// Sanity check pro payload vindo do LLM — evita explodir UI se ele inventa formato.
function isSupplierAnalysis(d: unknown): d is SupplierAnalysis {
  if (!d || typeof d !== 'object') return false
  const o = d as Record<string, unknown>
  return (
    typeof o.veredito === 'string' &&
    typeof o.resumo === 'string' &&
    Array.isArray(o.destaques)
  )
}

// Explorador de compras: lista priorizada por fornecedor, filtros de status, busca,
// IA por item/fornecedor e ações Aprovar/Bloquear/Monitorar. Antes era a aba
// "Sugestão de Compras"; agora é embutido no Miguel (sub-aba "Explorar"). Não
// renderiza AppLayout — quem embute fornece o layout.
export default function PurchaseExplorer() {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { rows, loading } = useReplenishment()
  const { notify } = useToast()
  const [qtyOverride, setQtyOverride] = useState<Record<string, number>>({})
  const [statusFilter, setStatusFilter] = useState<Set<CommercialStatus>>(
    new Set(['Compra urgente', 'Comprar', 'Monitorar'])
  )
  // Anti-ruptura: quando ligado, mostra só itens em risco de ruptura (em_risco).
  const [onlyAtRisk, setOnlyAtRisk] = useState(false)
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  // Explicações IA por item — estado por id pra permitir rodar em paralelo
  // sem perder o que já foi gerado.
  const { generate: aiGenerate } = useAiInsights()
  const [aiTextById, setAiTextById] = useState<Record<string, string>>({})
  const [aiBusyId, setAiBusyId] = useState<string | null>(null)
  // Análise consolidada por fornecedor — renderizada num modal gamificado.
  const [aiSupplierBusy, setAiSupplierBusy] = useState<string | null>(null)
  interface OpenAnalysis {
    supplierName: string
    totalItems: number
    subtotalBRL: number
    leadTimeDays: number | null
    analysis: SupplierAnalysis
  }
  const [openAnalysis, setOpenAnalysis] = useState<OpenAnalysis | null>(null)

  /**
   * Aplica as qty da composição extra (IA) na coluna Qtd dos produtos.
   *
   * Itens da composição podem estar em status fora do filtro atual
   * (ex: "Monitorar" se o usuário tirou esse chip). Detectamos esses casos
   * e ajustamos o filtro automaticamente pra que apareçam na tabela —
   * caso contrário a qty é setada mas o usuário não vê o efeito.
   */
  function aplicarComposicao(items: NonNullable<SupplierAnalysis['composicao_extra']>) {
    const bySku = new Map(rows.map(r => [r.sku, r]))
    const overridesUpdate: Record<string, number> = {}
    const statusesToInclude = new Set<CommercialStatus>()
    let aplicados = 0
    let foraDeStatus = 0

    for (const c of items) {
      const r = bySku.get(c.sku)
      if (!r) continue
      // Skip se já está aprovado ou tem pedido aberto — usuário precisa intervir manualmente
      if (r.kanban_status === 'Compra Aprovada' || r.active_order_number) continue
      overridesUpdate[r.id] = c.qty
      aplicados++
      if (!statusFilter.has(r.commercial_status)) {
        statusesToInclude.add(r.commercial_status)
        foraDeStatus++
      }
    }

    if (aplicados === 0) {
      notify('error', 'Nenhum item aplicável — já podem estar aprovados ou em pedido aberto.')
      return
    }

    setQtyOverride(prev => ({ ...prev, ...overridesUpdate }))
    if (statusesToInclude.size > 0) {
      setStatusFilter(prev => {
        const next = new Set(prev)
        statusesToInclude.forEach(s => next.add(s))
        return next
      })
    }

    const msg = foraDeStatus > 0
      ? `${aplicados} qty aplicadas · ${foraDeStatus} item(s) revelados ao incluir o status no filtro.`
      : `${aplicados} qty aplicadas na coluna Qtd. Revise e clique em Aprovar.`
    notify('success', msg)
  }

  // Conta só itens acionáveis (ver isActionable) — assim o número do chip bate com
  // o que a lista realmente mostra. Itens já com pedido aberto ou aprovados não
  // entram, porque também não aparecem em groups/candidates.
  const counts = useMemo(() => {
    const c = { 'Compra urgente': 0, 'Comprar': 0, 'Monitorar': 0, 'Não comprar': 0 } as Record<CommercialStatus, number>
    rows.forEach(r => { if (isActionable(r)) c[r.commercial_status]++ })
    return c
  }, [rows])

  // Anti-ruptura: itens em risco de zerar que ainda PRECISAM de ação (sem pedido
  // aberto, não aprovados). Alimenta o banner e o chip "Em risco".
  const atRiskCount = useMemo(
    () => rows.filter(r => r.em_risco && isActionable(r)).length,
    [rows],
  )

  // Itens candidatos: status selecionado, ainda sem pedido aberto, não aprovados.
  const groups = useMemo(() => {
    const q = search.trim().toLowerCase()
    const candidates = rows.filter(r =>
      statusFilter.has(r.commercial_status) &&
      (!onlyAtRisk || r.em_risco) &&
      isActionable(r) &&
      (!q || r.sku.toLowerCase().includes(q) || r.name.toLowerCase().includes(q))
    )
    const map = new Map<string, { supplierName: string; rows: ProductRow[] }>()
    candidates.forEach(r => {
      const key = r.suppliers?.id ?? '__none__'
      const supplierName = r.suppliers?.name ?? 'Sem fornecedor vinculado'
      if (!map.has(key)) map.set(key, { supplierName, rows: [] })
      map.get(key)!.rows.push(r)
    })
    // Ordena por status (urgente primeiro), depois por score desc; fornecedores pelo
    // primeiro item da lista já ordenada.
    const rank = (r: ProductRow) => STATUS_ORDER.indexOf(r.commercial_status)
    const arr = Array.from(map.values()).map(g => ({
      ...g,
      rows: [...g.rows].sort((a, b) => rank(a) - rank(b) || b.commercial_score - a.commercial_score),
    }))
    arr.sort((a, b) =>
      (rank(a.rows[0]) - rank(b.rows[0])) || ((b.rows[0]?.commercial_score ?? 0) - (a.rows[0]?.commercial_score ?? 0))
    )
    return arr
  }, [rows, statusFilter, onlyAtRisk, search])

  // Default da Qtd = total_suggestion (matriz + filiais vinculadas), alinhado
  // com a coluna "Sugestão Total" do Painel BX. Quando a empresa não tem filial,
  // total_suggestion == purchase_suggestion (sem efeito).
  // Anti-ruptura: sem ajuste manual, item em ruptura entra com o dobro (não re-romper no lead time).
  const qtyOf = (r: ProductRow) => qtyOverride[r.id] ?? aplicarDobraRuptura(r.total_suggestion, r.current_stock, r.kanban_status)

  const totalImpacto = useMemo(
    () => groups.reduce((sum, g) => sum + g.rows.reduce((s, r) => s + qtyOf(r) * r.cost, 0), 0),
    [groups, qtyOverride] // eslint-disable-line react-hooks/exhaustive-deps
  )

  // ── Cap mensal por CAIXA DO MÊS (Sprint 2) ───────────────────
  // O greedy avalia o impacto de caixa de cada item: projeta as parcelas que
  // SERIAM geradas no fornecedor e soma só o que cai no mês corrente. Itens
  // com prazo longo (Alva 30/60/90/120) consomem zero do mês — então cabem
  // mesmo num cap apertado.
  const { budget } = useCompanyBudget()
  const { sumByMonth } = useInstallments()
  const monthKeyAtual = new Date().toISOString().slice(0, 7)
  const today = useMemo(() => new Date(), [])
  const cap = useMemo(() => {
    const teto = budget.purchase_budget_monthly
    const ativo = teto > 0
    const caixaMes = ativo ? sumByMonth(monthKeyAtual) : 0
    const restante = ativo ? Math.max(0, teto - caixaMes) : Infinity
    const items: CashCapItem[] = groups.flatMap(g => g.rows.map(r => {
      const sup = r.suppliers
      const terms = sup
        ? [sup.payment_term_1, sup.payment_term_2, sup.payment_term_3, sup.payment_term_4, sup.payment_term_5, sup.payment_term_6]
            .filter((t): t is number => typeof t === 'number' && t > 0)
        : []
      return {
        id: r.id,
        total_cost: qtyOf(r) * r.cost,
        payment_terms: terms,
        urgencyRank: STATUS_ORDER.indexOf(r.commercial_status),
        abcRank: r.abc_curve === 'A' ? 0 : r.abc_curve === 'B' ? 1 : r.abc_curve === 'C' ? 2 : 3,
        score: r.commercial_score,
      }
    }))
    const partition = splitByCashCap(items, restante, monthKeyAtual, today)
    return { ativo, teto, caixaMes, restante, partition }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, qtyOverride, budget.purchase_budget_monthly, sumByMonth, monthKeyAtual, today])

  const aposCorte = isAfterCutoff(today, budget.purchase_cutoff_day)

  function toggleStatus(s: CommercialStatus) {
    setStatusFilter(prev => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s); else next.add(s)
      return next
    })
  }

  async function mutate(r: ProductRow, update: Partial<Record<string, unknown>>, msg: string) {
    setBusy(r.id)
    const { error } = await supabase.from('products').update(update).eq('id', r.id)
    setBusy(null)
    if (error) notify('error', `Erro: ${error.message}`)
    else notify('success', msg) // realtime do useReplenishment atualiza a lista
  }

  const aprovar = (r: ProductRow) => {
    const qty = Math.floor(qtyOf(r))
    if (qty <= 0) { notify('error', 'Quantidade deve ser maior que zero.'); return }
    return mutate(r, { approval_input: String(qty), approved_qty: qty, kanban_status: 'Compra Aprovada' as KanbanStatusOp }, `${r.name}: aprovado (${qty}).`)
  }
  const bloquear = (r: ProductRow) =>
    mutate(r, { approval_input: 'B', approved_qty: 0, kanban_status: 'Bloqueado' as KanbanStatusOp }, `${r.name}: bloqueado.`)
  const monitorar = (r: ProductRow) =>
    mutate(r, { approval_input: 'S', approved_qty: 0, kanban_status: 'Standby' as KanbanStatusOp }, `${r.name}: em monitoramento.`)

  async function analisarFornecedor(supplierName: string, rowsOfSupplier: ProductRow[]) {
    setAiSupplierBusy(supplierName)
    try {
      const statusBreakdown = rowsOfSupplier.reduce<Record<string, number>>((acc, r) => {
        acc[r.commercial_status] = (acc[r.commercial_status] ?? 0) + 1
        return acc
      }, {})
      const subtotal = rowsOfSupplier.reduce((s, r) => s + qtyOf(r) * r.cost, 0)
      const supplier = rowsOfSupplier[0]?.suppliers
      const leadTime = supplier?.lead_time_days ?? null
      const minOrder = supplier?.min_order ?? null
      // Prazos de pagamento do fornecedor — até 6 colunas no schema, filtramos > 0.
      const paymentTerms = supplier
        ? [supplier.payment_term_1, supplier.payment_term_2, supplier.payment_term_3,
           supplier.payment_term_4, supplier.payment_term_5, supplier.payment_term_6]
            .filter((d): d is number => typeof d === 'number' && d > 0)
        : []

      // Itens já no carrinho (vão pro top 15 por urgência no backend).
      // qty_atual = qty que o usuário tem agora; purchase_suggestion (campo no
      // payload da IA, mantido por compat) carrega total_suggestion = matriz +
      // filial — alinhado com a coluna Sug. Total do Painel BX.
      const items = rowsOfSupplier.map(r => ({
        sku: r.sku,
        name: r.name,
        status: r.commercial_status,
        priority: r.priority,
        abc_curve: r.abc_curve,
        current_stock: r.current_stock,
        coverage_days: r.coverage_days,
        daily_avg: r.daily_avg,
        qty_atual: qtyOf(r),
        purchase_suggestion: r.total_suggestion,
        cost: r.cost,
        days_without_sale: r.days_without_sale ?? null,
      }))

      // Itens DISPONÍVEIS pra completar mínimo: mesmo fornecedor, NÃO no carrinho,
      // sem encalhe, status diferente de "Não comprar". Cappa em 30 pra não inflar
      // payload de IA. Backend faz greedy A→B→C em cima dessa lista.
      const supplierId = supplier?.id
      const inCart = new Set(rowsOfSupplier.map(r => r.id))
      const itemsDisponiveisExtra = supplierId
        ? rows
            .filter(r => r.suppliers?.id === supplierId && !inCart.has(r.id))
            .filter(r => !r.encalhe && r.commercial_status !== 'Não comprar')
            .filter(r => r.total_suggestion > 0 && r.cost > 0)
            .slice(0, 30)
            .map(r => ({
              sku: r.sku,
              name: r.name,
              abc_curve: r.abc_curve,
              cost: r.cost,
              qty_sugerida: r.total_suggestion,
              status: r.commercial_status,
              coverage_days: r.coverage_days,
            }))
        : []

      const res = await aiGenerate('purchase_supplier', {
        supplier: supplierName,
        lead_time: leadTime,
        min_order: minOrder,
        payment_terms: paymentTerms,
        today_iso: new Date().toISOString().slice(0, 10),
        n_items: rowsOfSupplier.length,
        subtotal_estimado: subtotal,
        status_breakdown: statusBreakdown,
        items,
        items_disponiveis_extra: itemsDisponiveisExtra,
      })
      if (res.ok && res.data && isSupplierAnalysis(res.data)) {
        setOpenAnalysis({
          supplierName,
          totalItems: rowsOfSupplier.length,
          subtotalBRL: subtotal,
          leadTimeDays: leadTime,
          analysis: res.data,
        })
      } else if (res.ok && res.text) {
        // Fallback raro: LLM não devolveu JSON parseável — mostra como toast.
        notify('error', `IA respondeu fora do formato esperado. Tente de novo.`)
      } else {
        notify('error', res.error ?? 'Falha ao analisar fornecedor.')
      }
    } finally {
      setAiSupplierBusy(null)
    }
  }

  async function explicar(r: ProductRow) {
    if (aiTextById[r.id]) {
      // Toggle off — fecha a explicação já gerada
      setAiTextById(prev => {
        const next = { ...prev }
        delete next[r.id]
        return next
      })
      return
    }
    setAiBusyId(r.id)
    try {
      const res = await aiGenerate('purchase_item', {
        sku: r.sku,
        name: r.name,
        supplier: r.suppliers?.name ?? null,
        commercial_status: r.commercial_status,
        commercial_score: r.commercial_score,
        current_stock: r.current_stock,
        coverage_days: r.coverage_days,
        daily_avg: r.daily_avg,
        monthly_avg: r.monthly_avg,
        lead_time: r.suppliers?.lead_time_days ?? null,
        reorder_point: r.reorder_point,
        estoque_max: r.estoque_max,
        purchase_suggestion: r.total_suggestion,
        open_qty: r.open_qty,
        cost: r.cost,
        margin_pct: r.margin_pct ?? null,
        abc_curve: r.abc_curve ?? null,
        days_without_sale: r.days_without_sale ?? null,
        priority: r.priority,
      })
      if (res.ok && res.text) setAiTextById(prev => ({ ...prev, [r.id]: res.text! }))
      else notify('error', res.error ?? 'Falha ao gerar explicação.')
    } finally {
      setAiBusyId(null)
    }
  }

  if (isMobile) {
    return (
      <>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* Filtros de status — grid 2 cols */}
            <div className="grid grid-cols-2 gap-2">
              {STATUS_ORDER.map(s => {
                const active = statusFilter.has(s)
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={`h-11 rounded-2xl text-xs font-bold flex items-center justify-center gap-1.5 touch-action-manip ${commercialStatusColor[s]} ${active ? 'ring-2 ring-ink/50' : 'opacity-60'}`}
                  >
                    <span>{s}</span>
                    <span className="bg-black/10 dark:bg-white/10 rounded-full px-1.5 text-[10px] tabular-nums">{counts[s]}</span>
                  </button>
                )
              })}
              {/* Anti-ruptura: só itens em risco de zerar */}
              <button
                onClick={() => setOnlyAtRisk(v => !v)}
                className={`h-11 rounded-2xl text-xs font-bold flex items-center justify-center gap-1.5 touch-action-manip col-span-2 border ${
                  onlyAtRisk
                    ? 'bg-rose-600 text-white border-rose-700 ring-2 ring-rose-300'
                    : 'bg-rose-50 text-rose-800 border-rose-300 dark:bg-rose-500/10 dark:text-rose-200 dark:border-rose-500/30'
                }`}
              >
                <span>⚠️ Em risco de ruptura</span>
                <span className="bg-black/10 dark:bg-white/10 rounded-full px-1.5 text-[10px] tabular-nums">{atRiskCount}</span>
              </button>
            </div>
            {/* Impacto (herói) + busca */}
            <div className="rounded-2xl p-4 bg-gradient-to-br from-brand-700 to-brand-900 border border-brand-500/40">
              <p className="text-[11px] text-cream/70 uppercase tracking-[0.18em]">Impacto estimado</p>
              <p className="mt-1 text-3xl font-extrabold tabular-nums text-accent leading-none">{fmtMoney(totalImpacto)}</p>
              <p className="mt-1.5 text-[11px] text-cream/60">
                {groups.length} {groups.length === 1 ? 'fornecedor' : 'fornecedores'} · {counts['Compra urgente']} urgente{counts['Compra urgente'] === 1 ? '' : 's'}
              </p>
            </div>
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar produto…"
              className="w-full h-11 bg-surface border border-line rounded-2xl px-3 text-ink text-sm placeholder:text-ink-faint"
            />
            {groups.length === 0 ? (
              <div className="py-16 text-center text-ink-faint">
                <p className="text-2xl mb-2">🛒</p>
                <p>Nenhum item nos status selecionados.</p>
              </div>
            ) : (
              <MobilePurchaseSuggestion
                groups={groups}
                qtyOf={qtyOf}
                setQty={(id, qty) => setQtyOverride(prev => ({ ...prev, [id]: qty }))}
                aiSupplierBusy={aiSupplierBusy}
                aiBusyId={aiBusyId}
                aiTextById={aiTextById}
                busy={busy}
                onAnalisarFornecedor={analisarFornecedor}
                onExplicar={explicar}
                onAprovar={aprovar}
                onMonitorar={monitorar}
                onBloquear={bloquear}
              />
            )}
          </div>
        )}
        {openAnalysis && (
          <SupplierAnalysisModal
            supplierName={openAnalysis.supplierName}
            totalItems={openAnalysis.totalItems}
            subtotalBRL={openAnalysis.subtotalBRL}
            leadTimeDays={openAnalysis.leadTimeDays}
            analysis={openAnalysis.analysis}
            onApplyComposicao={aplicarComposicao}
            onClose={() => setOpenAnalysis(null)}
          />
        )}
      </>
    )
  }

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-base-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Filtros por status + busca + impacto */}
          <div className="grid grid-cols-1 lg:grid-cols-3 items-center gap-3">
            <div className="flex flex-wrap gap-2 lg:col-span-2">
              {STATUS_ORDER.map(s => {
                const active = statusFilter.has(s)
                return (
                  <button
                    key={s}
                    onClick={() => toggleStatus(s)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${commercialStatusColor[s]} ${active ? 'ring-2 ring-ink/50' : 'opacity-60 hover:opacity-100'}`}
                  >
                    <span>{s}</span>
                    <span className="bg-black/10 dark:bg-white/10 rounded-full px-1.5 py-0.5 text-xs tabular-nums">{counts[s]}</span>
                  </button>
                )
              })}
              {/* Anti-ruptura: filtra só os itens em risco de zerar */}
              <button
                onClick={() => setOnlyAtRisk(v => !v)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-all border ${
                  onlyAtRisk
                    ? 'bg-rose-600 text-white border-rose-700 ring-2 ring-rose-300'
                    : 'bg-rose-50 text-rose-800 border-rose-300 dark:bg-rose-500/10 dark:text-rose-200 dark:border-rose-500/30 hover:brightness-105'
                }`}
                title="Mostrar só itens em risco de ruptura — vão zerar dentro do lead time + colchão e ainda sem pedido"
              >
                <span>⚠️ Em risco</span>
                <span className="bg-black/10 dark:bg-white/10 rounded-full px-1.5 py-0.5 text-xs tabular-nums">{atRiskCount}</span>
              </button>
            </div>
            <div className="flex justify-end">
              <div className="px-4 py-2 rounded-lg bg-gradient-to-br from-brand-700 to-brand-900 border border-brand-500/40 text-right">
                <p className="text-[11px] text-cream/70 uppercase tracking-wider">Impacto estimado</p>
                <p className="text-lg font-bold text-accent tabular-nums">{fmtMoney(totalImpacto)}</p>
              </div>
            </div>
          </div>

          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar SKU ou produto..."
            className="w-full bg-surface border border-line rounded px-3 py-1.5 text-ink text-sm placeholder:text-ink-faint"
          />

          {/* Banner anti-ruptura: itens que vão zerar e ainda sem pedido */}
          {atRiskCount > 0 && !onlyAtRisk && (
            <button
              onClick={() => setOnlyAtRisk(true)}
              className="w-full flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm text-left bg-rose-50 border-rose-300 text-rose-900 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-200 hover:brightness-105 transition"
            >
              <span>
                ⚠️ <strong>{atRiskCount}</strong> {atRiskCount === 1 ? 'item em risco de ruptura' : 'itens em risco de ruptura'} — vão zerar dentro do lead time + colchão e ainda sem pedido em aberto.
              </span>
              <span className="text-xs font-semibold underline whitespace-nowrap">Ver só esses</span>
            </button>
          )}

          {/* Banner do cap mensal — só quando há orçamento cadastrado */}
          {cap.ativo && (
            <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm ${
              cap.partition.fitsCount < cap.partition.totalCount
                ? 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200'
                : 'bg-emerald-50 border-emerald-300 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-200'
            }`}>
              <span>
                <strong>{cap.partition.fitsCount}</strong> de <strong>{cap.partition.totalCount}</strong> itens cabem no caixa do mês — restam <strong>{fmtMoney(cap.restante)}</strong> de {fmtMoney(cap.teto)}
              </span>
              <span className="text-xs">
                Caixa do mês já comprometido: <strong>{fmtMoney(cap.caixaMes)}</strong>
                {aposCorte && <span className="ml-2 px-2 py-0.5 rounded bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-100 text-[10px] font-bold">Após dia {budget.purchase_cutoff_day} · novas compras devem ir como Standby</span>}
              </span>
            </div>
          )}

          {groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-ink-faint gap-2">
              <p className="text-base">Nenhum item nos status selecionados.</p>
              <p className="text-sm text-ink-faint">Ajuste os filtros acima.</p>
            </div>
          ) : groups.map(g => {
            const subtotal = g.rows.reduce((s, r) => s + qtyOf(r) * r.cost, 0)
            return (
              <div key={g.supplierName} className="bg-surface border border-line rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-line flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-ink font-semibold">{g.supplierName}</h3>
                    <p className="text-xs text-ink-faint mt-0.5">{g.rows.length} itens · lead time {g.rows[0]?.suppliers?.lead_time_days ?? '—'} dias</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => analisarFornecedor(g.supplierName, g.rows)}
                      disabled={aiSupplierBusy === g.supplierName}
                      className={`text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all ${AI_BTN}`}
                      title="Análise via IA do pacote inteiro deste fornecedor — cita SKUs e recomenda ação"
                    >
                      {aiSupplierBusy === g.supplierName ? (
                        <>
                          <span className="w-3 h-3 border border-brand-900/50 border-t-transparent rounded-full animate-spin" />
                          Analisando...
                        </>
                      ) : (
                        <>✨ Analisar fornecedor</>
                      )}
                    </button>
                    <div className="text-right">
                      <p className="text-[11px] text-ink-faint uppercase">Subtotal</p>
                      <p className="text-base font-semibold text-ink tabular-nums">{fmtMoney(subtotal)}</p>
                    </div>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-surface border-b border-line">
                    <tr className="text-left text-xs font-semibold text-ink-muted uppercase">
                      <th className="px-3 py-2 hidden lg:table-cell w-[100px]">SKU</th>
                      <th className="px-3 py-2">Produto</th>
                      <th className="px-3 py-2 text-right w-[64px]">Estq.</th>
                      <th className="px-3 py-2 text-right hidden xl:table-cell w-[80px]">Ped. ab.</th>
                      <th className="px-3 py-2 text-right hidden xl:table-cell w-[70px]">
                        <span className="cursor-help border-b border-dotted border-line" title="Estoque máximo = lead time × média de venda diária.">Máx</span>
                      </th>
                      <th className="px-3 py-2 text-right w-[70px]">
                        <span className="cursor-help border-b border-dotted border-line" title="Quanto comprar para repor: ponto de reposição − estoque atual − pedido em aberto.">Sugest.</span>
                      </th>
                      <th className="px-3 py-2 text-center hidden xl:table-cell w-[56px]">
                        <span className="cursor-help border-b border-dotted border-line" title="Score comercial 0–100: pondera curva ABC, cobertura em dias, margem, venda acelerando e ruptura.">Score</span>
                      </th>
                      <th className="px-3 py-2 hidden md:table-cell w-[120px]">Status</th>
                      <th className="px-3 py-2 text-right w-[76px]">Qtd</th>
                      <th className="px-3 py-2 text-right w-[170px]">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.flatMap(r => {
                      const naoCabe = cap.ativo && !cap.partition.fitsIds.has(r.id)
                      return [
                      <tr key={r.id} className={`border-b border-line ${naoCabe ? 'bg-amber-50/60 dark:bg-amber-500/5' : ''}`}>
                        <td className="px-3 py-2 font-mono text-xs text-ink-muted truncate hidden lg:table-cell" title={r.sku}>{r.sku}</td>
                        <td className="px-3 py-2 text-ink min-w-0">
                          <p className="truncate" title={r.name}>{r.name}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`md:hidden inline-block text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${commercialStatusColor[r.commercial_status]}`}>{r.commercial_status}</span>
                            {r.demanda_prevista > r.monthly_avg * 1.05 && (
                              <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300" title={`Demanda acelerando: previsão ${fmtNum1(r.demanda_prevista)}/mês vs média ${fmtNum1(r.monthly_avg)}/mês — sugestão antecipada para evitar ruptura.`}>
                                📈 demanda
                              </span>
                            )}
                            {naoCabe && (
                              <span className="inline-block text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-100" title="Não cabe no orçamento deste mês — sugestão é deixar para a virada">
                                ↪ virada
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums ${r.current_stock <= 0 ? 'text-danger font-semibold' : 'text-ink-muted'}`}>{r.current_stock}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-muted hidden xl:table-cell">{r.open_qty || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-muted hidden xl:table-cell">{fmtNum1(r.estoque_max)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink font-medium">
                          {r.total_suggestion}
                          {r.filial_suggestion > 0 && (
                            <span className="block text-[10px] text-ink-faint font-normal leading-none mt-0.5" title="Matriz + Filiais">
                              M{r.purchase_suggestion} + F{r.filial_suggestion}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center tabular-nums text-ink hidden xl:table-cell">{r.commercial_score}</td>
                        <td className="px-3 py-2 hidden md:table-cell whitespace-nowrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${commercialStatusColor[r.commercial_status]}`}>{r.commercial_status}</span>
                          {isRupturaAcionavel(r.current_stock, r.kanban_status) && (
                            <span
                              className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30 align-middle"
                              title="Item em ruptura: quantidade dobrada para não re-romper dentro do lead time. Edite a quantidade para ajustar."
                            >
                              ×2
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number" min={0}
                            value={qtyOf(r)}
                            onChange={e => setQtyOverride(prev => ({ ...prev, [r.id]: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))}
                            className="w-16 bg-surface border border-line rounded px-2 py-1 text-right tabular-nums text-sm text-ink"
                          />
                        </td>
                        <td className="px-2 py-2 text-right">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            {aposCorte ? (
                              <>
                                <button onClick={() => monitorar(r)} disabled={busy === r.id} className="text-xs px-2 py-1 bg-amber-500/15 hover:bg-amber-500/25 disabled:opacity-50 text-amber-800 dark:text-amber-300 rounded border border-amber-500/40 whitespace-nowrap" title="Após dia de corte: vai como Standby para a virada do mês">Próximo mês</button>
                                <button onClick={() => aprovar(r)} disabled={busy === r.id} className="text-xs px-2 py-1 bg-brand-600/15 hover:bg-brand-600/25 disabled:opacity-50 text-brand-700 dark:text-brand-300 rounded border border-brand-600/40 whitespace-nowrap" title="Antecipa para o mês atual mesmo após o dia de corte">Antecipar</button>
                              </>
                            ) : (
                              <button onClick={() => aprovar(r)} disabled={busy === r.id} className="text-xs px-2 py-1 bg-brand-600/15 hover:bg-brand-600/25 disabled:opacity-50 text-brand-700 dark:text-brand-300 rounded border border-brand-600/40 whitespace-nowrap">Aprovar</button>
                            )}
                            <button onClick={() => monitorar(r)} disabled={busy === r.id} className="text-xs px-2 py-1 bg-sunken hover:bg-line disabled:opacity-50 text-ink-muted rounded border border-line whitespace-nowrap">Monitorar</button>
                            <button onClick={() => bloquear(r)} disabled={busy === r.id} className="text-xs px-2 py-1 bg-danger/10 hover:bg-danger/20 disabled:opacity-50 text-danger rounded border border-danger/40 whitespace-nowrap">Bloquear</button>
                            <button
                              onClick={() => explicar(r)}
                              disabled={aiBusyId === r.id}
                              className={`text-xs px-2 py-1 rounded whitespace-nowrap ${AI_BTN}`}
                              title="Explica via IA por que comprar este item"
                            >
                              {aiBusyId === r.id ? '...' : aiTextById[r.id] ? 'Fechar' : '✨'}
                            </button>
                          </div>
                        </td>
                      </tr>,
                      aiTextById[r.id] ? (
                        <tr key={`${r.id}-ai`} className={AI_PANEL}>
                          <td colSpan={10} className="px-4 py-3">
                            <div className="flex items-start gap-2">
                              <span className={`text-[10px] font-semibold uppercase tracking-wider shrink-0 mt-0.5 ${AI_LABEL}`}>✨ IA</span>
                              <p className="text-sm text-ink leading-relaxed whitespace-pre-line">{aiTextById[r.id]}</p>
                            </div>
                          </td>
                        </tr>
                      ) : null,
                      ]
                    })}
                  </tbody>
                </table>
              </div>
            )
          })}

          <p className="text-xs text-ink-faint">
            Itens aprovados vão para <button onClick={() => navigate('/relatorio-compras')} className="underline hover:text-ink-muted">Emissão de Pedidos</button>, onde viram pedido de compra por fornecedor.
          </p>
        </div>
      )}
      {openAnalysis && (
        <SupplierAnalysisModal
          supplierName={openAnalysis.supplierName}
          totalItems={openAnalysis.totalItems}
          subtotalBRL={openAnalysis.subtotalBRL}
          leadTimeDays={openAnalysis.leadTimeDays}
          analysis={openAnalysis.analysis}
          onApplyComposicao={aplicarComposicao}
          onClose={() => setOpenAnalysis(null)}
        />
      )}
    </>
  )
}
